"use client";

import { useSession } from "next-auth/react";
import { useEffect, useState } from "react";
import PeeqdTopbar from "@/components/peeqd/PeeqdTopbar";
import { PeeqdPricing, PeeqdFAQ, PeeqdFooter } from "@/components/peeqd/PeeqdMarketing";
import { peeqdPageTopbar } from "@/config/peeqdContent";
import LoadingSpinner from "@/components/LoadingSpinner";
import apiClient from "@/libs/api";
import { trackFunnelEvent } from "@/libs/ga";

interface UserData {
  subscription: {
    priceId: string | null;
    status: string;
  };
}

const topbarContent = peeqdPageTopbar("Pricing");

export default function PricingPage() {
  const { status } = useSession();
  const [userData, setUserData] = useState<UserData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchUserData = async () => {
      if (status === "authenticated") {
        try {
          const data = await apiClient.get("/user/me");
          setUserData(data as unknown as UserData);
        } catch (error) {
          console.error("Error fetching user data:", error);
        } finally {
          setIsLoading(false);
        }
      } else if (status === "unauthenticated") {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [status]);

  useEffect(() => {
    if (status === "loading" || isLoading) return;
    trackFunnelEvent("monetization", "pricing_page_loaded", {
      layout: "peeqd",
      has_current_subscription: userData?.subscription?.status === "active",
      current_price_id: userData?.subscription?.priceId ?? null,
      source: "pricing_page",
    });
  }, [status, isLoading, userData?.subscription?.status, userData?.subscription?.priceId]);

  if (status === "loading" || isLoading) {
    return <LoadingSpinner fullScreen />;
  }

  const currentUserPriceId =
    userData?.subscription?.status === "active"
      ? userData?.subscription?.priceId || null
      : null;

  return (
    <>
      <PeeqdTopbar content={topbarContent} showToolPills={false} />
      <main className="px-4 sm:px-6 lg:px-10">
        <div className="mx-auto max-w-6xl pt-14 text-center">
          <h1 className="font-display text-[30px] font-extrabold tracking-tight sm:text-[38px]">
            One simple pricing plan. Pick yours.
          </h1>
          <p className="mt-3 text-[15.5px] text-muted">
            Weekly, monthly, or annual - same full tracking on every interval. Cancel anytime.
          </p>
        </div>
        <PeeqdPricing
          currentUserPriceId={currentUserPriceId}
          className="pb-14 pt-10"
          priceLayout="interval-weekly"
        />
        <PeeqdFAQ />
        <PeeqdFooter />
      </main>
    </>
  );
}
